import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { PageHeading, Modal } from '../components/ui';
import CalendarGrid from '../components/menuScheduling/CalendarGrid';
import RecipeSearchModal from '../components/menuScheduling/RecipeSearchModal';
import menuSchedulingService from '../services/menuSchedulingService';
import EducationModal from '../components/EducationModal';
import Toast from '../components/Toast';
import {
    generateGroceryListFromSchedule,
    createOccasionFromSchedule,
    createMenuPlanFromSchedule
} from '../services/unifiedPlanningService';

/**
 * MenuSchedulingPage Component
 * Main page for menu scheduling with 30-day rolling calendar
 * 
 * Premium features:
 * - Week selection for bulk actions
 * - Generate grocery list from week
 * - Create menu plan from week
 * - Create occasion from week
 */
const MenuSchedulingPage = () => {
    const { user, userProfile } = useAuth();
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState(new Date());
    const [scheduledMeals, setScheduledMeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [showHowToUseModal, setShowHowToUseModal] = useState(false);
    
    // Week selection state for bulk actions
    const [selectedWeekStart, setSelectedWeekStart] = useState(null);
    const [showWeekActions, setShowWeekActions] = useState(false);
    const [weekActionLoading, setWeekActionLoading] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success');
    const [showOccasionPrompt, setShowOccasionPrompt] = useState(false);
    const [occasionName, setOccasionName] = useState('');
    
    // Get background option from user profile or localStorage, default to 'tomato'
    const backgroundOption = userProfile?.menu_scheduling_settings?.background_option 
        || localStorage.getItem('menuSchedulingBackground') 
        || 'tomato';

    // Calculate end date (30 days from start)
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 30);

    // Load scheduled meals
    useEffect(() => {
        if (user) {
            loadScheduledMeals();
        }
        
        // Listen for occasion deletion events to refresh data
        const handleOccasionDeleted = () => {
            if (user) {
                loadScheduledMeals();
            }
        };
        
        window.addEventListener('occasionDeleted', handleOccasionDeleted);
        // Also refresh when page becomes visible (user switches back to tab)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && user) {
                loadScheduledMeals();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            window.removeEventListener('occasionDeleted', handleOccasionDeleted);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [user, startDate]);

    const loadScheduledMeals = async () => {
        if (!user) return;

        try {
            setLoading(true);
            setError(null);
            const token = await user.getIdToken();
            const meals = await menuSchedulingService.getScheduledMeals(
                startDate,
                endDate,
                token
            );
            setScheduledMeals(meals);
        } catch (err) {
            console.error('Error loading scheduled meals:', err);
            setError('Failed to load scheduled meals');
        } finally {
            setLoading(false);
        }
    };

    const handleDayClick = (date) => {
        setSelectedDate(date);
        setSearchModalOpen(true);
    };
    
    // Get existing meals for the selected date
    const getExistingMealsForDate = (date) => {
        if (!date) return [];
        const dateStr = date.toISOString().split('T')[0];
        return scheduledMeals.filter(meal => {
            const mealDate = new Date(meal.scheduled_date).toISOString().split('T')[0];
            return mealDate === dateStr;
        });
    };

    const handleRecipeAdded = () => {
        // Reload scheduled meals after adding
        loadScheduledMeals();
    };

    const handleCloseModal = () => {
        setSearchModalOpen(false);
        setSelectedDate(null);
    };

    // Week selection handlers for bulk actions
    const handleWeekSelect = (weekStartDate) => {
        if (selectedWeekStart && selectedWeekStart.getTime() === weekStartDate.getTime()) {
            // Deselect if clicking same week
            setSelectedWeekStart(null);
            setShowWeekActions(false);
        } else {
            setSelectedWeekStart(weekStartDate);
            setShowWeekActions(true);
        }
    };

    // Get week end date (7 days from start)
    const getWeekEnd = (weekStart) => {
        const end = new Date(weekStart);
        end.setDate(end.getDate() + 6);
        return end;
    };

    // Get meals in selected week
    const getMealsInWeek = () => {
        if (!selectedWeekStart) return [];
        const weekEnd = getWeekEnd(selectedWeekStart);
        return scheduledMeals.filter(meal => {
            const mealDate = new Date(meal.scheduled_date);
            return mealDate >= selectedWeekStart && mealDate <= weekEnd;
        });
    };

    // Format date range for display
    const formatDateRange = (start, end) => {
        const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${startStr} - ${endStr}`;
    };

    // Generate grocery list from selected week
    const handleGenerateGroceryList = async () => {
        if (!user || !selectedWeekStart) return;
        
        const weekEnd = getWeekEnd(selectedWeekStart);
        const mealsInWeek = getMealsInWeek();
        
        if (mealsInWeek.length === 0) {
            setToastType('error');
            setToastMessage('No meals scheduled in selected week');
            return;
        }

        try {
            setWeekActionLoading(true);
            const token = await user.getIdToken();
            const result = await generateGroceryListFromSchedule(
                selectedWeekStart.toISOString().split('T')[0],
                weekEnd.toISOString().split('T')[0],
                {},
                token
            );
            setToastType('success');
            setToastMessage(`✓ Created grocery list with ${result.items?.length || 0} items`);
            // Navigate to grocery list
            navigate('/grocery-list');
        } catch (err) {
            console.error('Error generating grocery list:', err);
            setToastType('error');
            setToastMessage('Failed to generate grocery list');
        } finally {
            setWeekActionLoading(false);
        }
    };

    // Create menu plan from selected week
    const handleCreateMenuPlan = async () => {
        if (!user || !selectedWeekStart) return;
        
        const weekEnd = getWeekEnd(selectedWeekStart);
        const mealsInWeek = getMealsInWeek();
        
        if (mealsInWeek.length === 0) {
            setToastType('error');
            setToastMessage('No meals scheduled in selected week');
            return;
        }

        try {
            setWeekActionLoading(true);
            const token = await user.getIdToken();
            const planName = `Week of ${selectedWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            const result = await createMenuPlanFromSchedule(
                selectedWeekStart.toISOString().split('T')[0],
                weekEnd.toISOString().split('T')[0],
                planName,
                token
            );
            setToastType('success');
            setToastMessage(`✓ Created menu plan "${result.name}"`);
            // Navigate to menu planning with the new plan
            navigate(`/menu-planning?planId=${result.id}`);
        } catch (err) {
            console.error('Error creating menu plan:', err);
            setToastType('error');
            setToastMessage('Failed to create menu plan');
        } finally {
            setWeekActionLoading(false);
        }
    };

    // Create occasion from selected week
    const handleCreateOccasion = () => {
        const mealsInWeek = getMealsInWeek();
        if (mealsInWeek.length === 0) {
            setToastType('error');
            setToastMessage('No meals scheduled in selected week');
            return;
        }
        // Show occasion name prompt
        setShowOccasionPrompt(true);
    };

    const handleOccasionSubmit = async () => {
        if (!user || !selectedWeekStart || !occasionName.trim()) return;
        
        const weekEnd = getWeekEnd(selectedWeekStart);

        try {
            setWeekActionLoading(true);
            const token = await user.getIdToken();
            const result = await createOccasionFromSchedule(
                selectedWeekStart.toISOString().split('T')[0],
                weekEnd.toISOString().split('T')[0],
                occasionName.trim(),
                token
            );
            setToastType('success');
            setToastMessage(`✓ Created occasion "${result.name}"`);
            setShowOccasionPrompt(false);
            setOccasionName('');
            // Navigate to occasions
            navigate('/occasions');
        } catch (err) {
            console.error('Error creating occasion:', err);
            setToastType('error');
            setToastMessage('Failed to create occasion');
        } finally {
            setWeekActionLoading(false);
        }
    };

    // Background image styles
    const getBackgroundStyle = () => {
        const backgroundImages = {
            tomato: '/images/menuScheduling/tomato_bg.png',
            lemon: '/images/menuScheduling/lemon_bg.png',
            spinach: '/images/menuScheduling/spinach_bg.png'
        };

        return {
            backgroundImage: `url(${backgroundImages[backgroundOption]})`,
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto',
            backgroundPosition: '0 0',
            position: 'relative'
        };
    };

    return (
        <div 
            className="min-h-screen"
            style={{ backgroundColor: '#F9F8F6' }}
        >
            <div className="w-full px-0 py-2 md:py-4">
                {/* Page Header */}
                <div className="page-heading-container hairline min-h-[32vh] md:min-h-[48vh] relative mb-8">
                    <div className="page-heading-content absolute bottom-0 left-2 right-2 md:left-4 md:right-4">
                        <div className="flex items-center gap-2">
                            <h1 className="font-sans text-hero text-neutral-900 tracking-tight font-medium">
                                MENU SCHEDULING
                            </h1>
                            <button
                                onClick={() => setShowHowToUseModal(true)}
                                className="flex items-center justify-center hairline bg-transparent hover:bg-neutral-200 transition-colors duration-120 cursor-pointer"
                                style={{ 
                                    height: '1em',
                                    width: '1em',
                                    lineHeight: '1',
                                    color: '#0D0D0D'
                                }}
                                aria-label="How to use Menu Scheduling"
                                onMouseEnter={(e) => {
                                    e.target.style.color = '#0D0D0D';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.color = '#0D0D0D';
                                }}
                            >
                                <svg 
                                    width="0.7em" 
                                    height="0.7em" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{ color: '#0D0D0D' }}
                                >
                                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                                    <path d="M12 17h.01"/>
                                </svg>
                            </button>
                        </div>
                        <p className="page-heading-subtitle font-mono uppercase tracking-wider text-neutral-900 opacity-60 mt-0 text-sm md:text-lg">
                            Plan your meals across the next 30 days
                        </p>
                    </div>
                </div>

                {/* Week Actions Bar - Show when week is selected */}
                {showWeekActions && selectedWeekStart && (
                    <div className="bg-neutral-0 hairline p-4 mb-4">
                        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <img 
                                    src={`/images/menuScheduling/${backgroundOption}_icon.png`}
                                    alt=""
                                    className="w-6 h-6"
                                    style={{ imageRendering: 'pixelated' }}
                                />
                                <div>
                                    <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                        Selected Week
                                    </p>
                                    <p className="font-sans text-lg font-medium text-[#1b263b]">
                                        {formatDateRange(selectedWeekStart, getWeekEnd(selectedWeekStart))}
                                    </p>
                                    <p className="font-mono text-xs text-neutral-900 opacity-60">
                                        {getMealsInWeek().length} meals scheduled
                                    </p>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    onClick={handleGenerateGroceryList}
                                    disabled={weekActionLoading || getMealsInWeek().length === 0}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-[#1b263b] text-white hairline hover:opacity-60 transition-all disabled:opacity-40 disabled:cursor-not-allowed border-none"
                                >
                                    🛒 Grocery List
                                </button>
                                <button
                                    onClick={handleCreateMenuPlan}
                                    disabled={weekActionLoading || getMealsInWeek().length === 0}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    ⏱️ Menu Plan
                                </button>
                                <button
                                    onClick={handleCreateOccasion}
                                    disabled={weekActionLoading || getMealsInWeek().length === 0}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    📋 Occasion
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedWeekStart(null);
                                        setShowWeekActions(false);
                                    }}
                                    className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-transparent text-neutral-900 opacity-60 hover:opacity-100 transition-all border-none"
                                >
                                    ✕ Clear
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <div className="bg-neutral-0 hairline minimal-padding vspace">
                    {/* Calendar Container */}
                    <div 
                        className="p-6 relative"
                        style={getBackgroundStyle()}
                    >
                        {error && (
                            <div className="mb-4 p-3 bg-red-50 text-red-900 hairline font-mono text-sm">
                                {error}
                            </div>
                        )}

                        {/* Week selection hint */}
                        {!showWeekActions && (
                            <div className="mb-4 p-3 bg-neutral-0 hairline">
                                <p className="font-mono text-xs uppercase tracking-wider text-neutral-900 opacity-60">
                                    💡 Tip: Click the week number to select a week for bulk actions
                                </p>
                            </div>
                        )}

                        {/* Content overlay - ensures calendar is above background */}
                        <div className="relative z-10">
                            {loading ? (
                                <div className="text-center py-12">
                                    <p className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60">
                                        Loading calendar...
                                    </p>
                                </div>
                            ) : (
                                <CalendarGrid
                                    startDate={startDate}
                                    scheduledMeals={scheduledMeals}
                                    onDayClick={handleDayClick}
                                    selectedDate={selectedDate}
                                    onWeekSelect={handleWeekSelect}
                                    selectedWeekStart={selectedWeekStart}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Recipe Search Modal */}
            <RecipeSearchModal
                isOpen={searchModalOpen}
                onClose={handleCloseModal}
                selectedDate={selectedDate}
                onRecipeAdded={handleRecipeAdded}
                backgroundOption={backgroundOption}
                existingMeals={selectedDate ? getExistingMealsForDate(selectedDate) : []}
            />

            {/* How to Use Modal */}
            <EducationModal
                isOpen={showHowToUseModal}
                onClose={() => setShowHowToUseModal(false)}
                title="How to Use Menu Scheduling"
                description="Plan your meals across the next 30 days with an interactive calendar."
                image={null}
                details={[
                    "Click any day in the calendar to add a recipe",
                    "Search by recipe name, ingredient, or vibe",
                    "Create new recipes directly from the search modal",
                    "Click week numbers to select for bulk actions",
                    "Generate grocery lists, menu plans, or occasions from weeks"
                ]}
            />

            {/* Occasion Name Prompt Modal */}
            {showOccasionPrompt && (
                <Modal
                    isOpen={showOccasionPrompt}
                    onClose={() => {
                        setShowOccasionPrompt(false);
                        setOccasionName('');
                    }}
                    maxWidth="max-w-md"
                >
                    <div className="p-6">
                        <h2 className="font-sans text-2xl font-medium text-[#1b263b] mb-4">
                            Create Occasion
                        </h2>
                        <p className="font-sans text-sm text-neutral-900 opacity-60 mb-4">
                            Create an occasion from {getMealsInWeek().length} scheduled meals
                        </p>
                        <input
                            type="text"
                            value={occasionName}
                            onChange={(e) => setOccasionName(e.target.value)}
                            placeholder="Occasion name (e.g., 'Thanksgiving Week')"
                            className="w-full px-4 py-3 bg-neutral-50 hairline font-sans text-base text-neutral-900 focus:outline-none focus:ring-2 focus:ring-[#1b263b] mb-4"
                            autoFocus
                        />
                        <div className="flex gap-2">
                            <button
                                onClick={handleOccasionSubmit}
                                disabled={!occasionName.trim() || weekActionLoading}
                                className="flex-1 px-4 py-3 font-mono text-sm uppercase tracking-wider bg-[#1b263b] text-white hover:opacity-60 transition-all disabled:opacity-40 disabled:cursor-not-allowed border-none"
                            >
                                {weekActionLoading ? 'Creating...' : 'Create Occasion'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowOccasionPrompt(false);
                                    setOccasionName('');
                                }}
                                className="px-4 py-3 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-all"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Toast Notifications */}
            {toastMessage && (
                <Toast
                    message={toastMessage}
                    type={toastType}
                    onClose={() => setToastMessage('')}
                    duration={3000}
                />
            )}
        </div>
    );
};

export default MenuSchedulingPage;

